import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

const MUTE_KEY = 'diy_voice_muted';

// Play a base64 mp3 clip. Returns a promise that resolves when playback ends.
function playBase64Mp3(base64) {
  return new Promise((resolve) => {
    try {
      const audio = new Audio(`data:audio/mpeg;base64,${base64}`);
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => resolve());
    } catch {
      resolve();
    }
  });
}

// Global mute state (localStorage-backed)
export function isMuted() {
  return localStorage.getItem(MUTE_KEY) === '1';
}

export function setMuted(v) {
  if (v) localStorage.setItem(MUTE_KEY, '1');
  else localStorage.removeItem(MUTE_KEY);
  window.dispatchEvent(new Event('diy-mute-change'));
}

export function useMuted() {
  const [muted, setMutedState] = useState(isMuted());
  useEffect(() => {
    const on = () => setMutedState(isMuted());
    window.addEventListener('diy-mute-change', on);
    return () => window.removeEventListener('diy-mute-change', on);
  }, []);
  return [muted, (v) => setMuted(v)];
}

// One shared, mutable ref so a new speak() cancels the previous one.
const currentAudioRef = { current: null };

function stopCurrent() {
  const cur = currentAudioRef.current;
  if (cur) {
    try { cur.pause(); } catch { /* ignore */ }
    currentAudioRef.current = null;
  }
}

async function playAudioBase64(base64) {
  if (!base64) return;
  stopCurrent();
  const audio = new Audio(`data:audio/mpeg;base64,${base64}`);
  currentAudioRef.current = audio;
  return new Promise((resolve) => {
    audio.onended = () => { if (currentAudioRef.current === audio) currentAudioRef.current = null; resolve(); };
    audio.onerror = () => { if (currentAudioRef.current === audio) currentAudioRef.current = null; resolve(); };
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => resolve());
  });
}

// Hook: buddy voice API for the current user.
export function useBuddyVoice() {
  const [muted] = useMuted();
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const speakQuip = useCallback(async (event, context = {}) => {
    if (mutedRef.current) return null;
    try {
      const res = await api.post('/buddy/quip', { event, context });
      if (mutedRef.current) return res.data; // muted after the request
      await playAudioBase64(res.data.audio_base64);
      return res.data;
    } catch { /* buddy quip failed — stay silent, not user-facing */
      return null;
    }
  }, []);

  // Play direct text (already provided by backend, e.g. a clarifying question)
  const speakText = useCallback(async (text) => {
    if (!text || mutedRef.current) return null;
    try {
      const res = await api.post('/tts', { text });
      if (mutedRef.current) return res.data;
      await playAudioBase64(res.data.audio_base64);
      return res.data;
    } catch { /* tts failed — stay silent, not user-facing */
      return null;
    }
  }, []);

  // Play pre-fetched base64 audio (e.g. clarify response with audio_base64 attached)
  const playBase64 = useCallback(async (b64) => {
    if (mutedRef.current) return;
    await playAudioBase64(b64);
  }, []);

  const stop = useCallback(() => stopCurrent(), []);

  return { speakQuip, speakText, playBase64, stop, muted };
}

// One-off util (non-hook) — useful outside components
export async function speakOnce(text) {
  if (isMuted() || !text) return;
  try {
    const res = await api.post('/tts', { text });
    await playAudioBase64(res.data.audio_base64);
  } catch { /* ignore */ }
}

export { playBase64Mp3 };
